
/**
 * Builds the rollup path for a given level.
 *
 * Annotations are only tagged at the lowest level (e.g. "tags").
 * To filter by a higher level (e.g. "subThemes"), the backend needs
 * to traverse the hierarchy bottom-to-top to find matching annotations.
 *
 * Example for [themes, subThemes, tags]:
 *   themes    → ["tags", "subThemes", "themes"]
 *   subThemes → ["tags", "subThemes"]
 */
const buildRollupPath = (hierarchyLevels, fromIndex) => {
  const levelsFromCurrentToBottom = hierarchyLevels.slice(fromIndex);
  const bottomToTop = [...levelsFromCurrentToBottom].reverse();
  return bottomToTop.map((l) => l.tagType);
};

const buildTarget = (level, index, hierarchyLevels) => {
  const isLowestLevel = index === hierarchyLevels.length - 1;

  if (isLowestLevel) {
    return { filterType: "tagType", tagType: level.tagType, isAnno: true };
  }

  return {
    filterType: "nestedRollupTagType",
    rollupPath: buildRollupPath(hierarchyLevels, index),
    isAnno: true,
  };
};

/**
 * Expands docHierarchyType config into all unique child filter configs for convertToOutput.
 * Collects configs from commonFilters, perDataset filters, and rollups.
 */
const expandDocHierarchyConfig = (config) => {
  const {
    commonFilters = [],
    sharedAcrossMultiple = [],
    commonDocumentFilters = [],
    perDataset = [],
  } = config?.source || {};
  const seen = new Set();
  const allConfigs = [];

  const addUnique = (f) => {
    if (f.disabledReason) return;
    if (!seen.has(f.filterId)) {
      allConfigs.push(f);
      seen.add(f.filterId);
    }
  };

  commonFilters.forEach(addUnique);
  for (const group of sharedAcrossMultiple) {
    (group.filters || []).forEach(addUnique);
  }
  commonDocumentFilters.forEach(addUnique);
  // Disabled datasets (disabledReason, see ootils enabledContentTypes) aren't in play, so their
  // filters don't count — e.g. cleanStaleActiveFilters drops them.
  for (const dataset of perDataset) {
    if (dataset.disabledReason) continue;
    (dataset.filters || []).forEach(addUnique);
    (dataset.rollups || []).forEach(addUnique);
    if (dataset.docFieldFilter) addUnique(dataset.docFieldFilter);
  }

  return allConfigs;
};

/**
 * Expands annoHierarchyType config into synthetic child configs for convertToOutput.
 * Uses source.hierarchyLevels to generate configs with correct filterIds.
 */
const expandAnnoHierarchyConfig = (config) => {
  const hierarchyLevels = config?.source?.hierarchyLevels;
  const commonAnnotationFilters = config?.source?.commonAnnotationFilters || [];
  // Split mode embeds the Topics (clustering-tree) filter config inside the
  // hierarchy config's source (for in-panel ordering); it must still expand
  // to a flat config so convertToOutput resolves its target by filterId.
  const topicClusteringFilterConfig = config?.source?.topicClusteringFilterConfig;

  const hierarchyConfigs =
    hierarchyLevels && Array.isArray(hierarchyLevels)
      ? hierarchyLevels.map((level, index) => ({
          filterId: level.filterId,
          // filterKey is what handleVizSegmentClick matches on; without it,
          // viz bar-click → filter integration silently no-ops for anno
          // hierarchy filters. Fall back to filterId when an explicit
          // filterKey wasn't provided at the level config.
          filterKey: level.filterKey || level.filterId,
          source: {
            filterType: "contentTagType",
            tagType: level.tagType,
          },
          target: buildTarget(level, index, hierarchyLevels),
        }))
      : [];

  const result = [
    ...hierarchyConfigs,
    ...(topicClusteringFilterConfig ? [topicClusteringFilterConfig] : []),
    ...commonAnnotationFilters,
  ];
  return result.length > 0 ? result : [config];
};

// Disabled items (disabledReason) are left out, so they never resolve to a query.
export const flattenFilterConfig = (filterConfig) => {
  return filterConfig.flatMap((filter) => {
    if (!filter.configs) {
      // Base case: direct filter
      // annoHierarchyType - expand using hierarchyLevels
      if (filter?.source?.filterType === "annoHierarchyType") {
        return expandAnnoHierarchyConfig(filter);
      }
      // docHierarchyType - expand using commonFilters + perDataset
      if (filter?.source?.filterType === "docHierarchyType") {
        return expandDocHierarchyConfig(filter);
      }
      return [filter];
    }

    const configItems = filter.configs;
    // Recursive case: flatten the configs array
    return configItems.flatMap((item) => {
      if (item.configs) {
        return flattenFilterConfig([item]);
      }
      // annoHierarchyType - expand using hierarchyLevels
      if (item?.source?.filterType === "annoHierarchyType") {
        return expandAnnoHierarchyConfig(item);
      }
      // docHierarchyType - expand using commonFilters + perDataset
      if (item?.source?.filterType === "docHierarchyType") {
        return expandDocHierarchyConfig(item);
      }
      return [item];
    });
  }).filter((f) => !f?.disabledReason);
};